import { z } from "zod";

/**
 * Shared view models for the Agents tab. Agents are auto-discovered from
 * spans via the `agents` materialized view (ClickHouse migration 043);
 * there's no "registered vs observed" distinction in v1.
 *
 * Identity columns mirror what the MV stores:
 *   - `id` — deterministic SHA1 of (team_id, project_id, identity_value).
 *   - `identityValue` — `agent.id` when set, else `agent.name`.
 *   - `identitySource` — which column was the source.
 */

export const AGENT_IDENTITY_SOURCES = ["agent_id", "agent_name"] as const;
export type AgentIdentitySource = (typeof AGENT_IDENTITY_SOURCES)[number];

export const AgentUuidSchema = z.string().min(1).max(128);

export interface AgentSpanIdentity {
  source: AgentIdentitySource;
  value: string;
}

export interface Agent {
  /** SHA1(team_id \0 project_id \0 identity_value). 40-char lowercase hex. */
  id: string;
  teamId: string;
  projectId: string;
  identityValue: string;
  identitySource: AgentIdentitySource;
  /** ISO-8601 UTC. */
  firstSeenAt: string;
  /** ISO-8601 UTC. */
  lastSeenAt: string;
}

/**
 * Per-day aggregate (window slice) for one agent. All token/span counts are
 * plain numbers; cost fields are strings to preserve ``Decimal64(10)``
 * precision over the wire.
 */
export interface AgentStatsSummary {
  identityValue: string;
  agentUuid: string;
  identitySource: AgentIdentitySource;
  executionCount: number;
  successCount: number;
  failureCount: number;
  unknownCount: number;
  spanCount: number;
  llmCallCount: number;
  toolCallCount: number;
  errorCount: number;
  llmSpanCount: number;
  toolSpanCount: number;
  errorSpanCount: number;
  blockedOnUserCount: number;
  blockedOnUserSpanCount: number;
  /** LLM spans with token usage but no resolved price; see migration 084. */
  unpricedLlmSpanCount: number;
  totalCost: string;
  inputCost: string;
  outputCost: string;
  cacheReadCost: string;
  cacheWriteCost: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  p50DurationNs: string;
  p95DurationNs: string;
  p95LlmDurationNs: string;
  p95ToolDurationNs: string;
  topModels: string[];
  topProviders: string[];
  topModel: string;
  topProvider: string;
  lastSeenAt: string;
}

export const AGENT_SERIES_GRANULARITIES = [
  "1m",
  "1h",
  "1d",
  "3d",
  "1w",
] as const;
export type AgentSeriesGranularity =
  (typeof AGENT_SERIES_GRANULARITIES)[number];

export interface AgentExecutionSeriesPoint {
  time: string;
  executionCount: number;
}

export interface AgentTimeseriesPoint extends AgentExecutionSeriesPoint {
  executionCount: number;
  successCount: number;
  failureCount: number;
  unknownCount: number;
  errorCount: number;
  spanCount: number;
  llmSpanCount: number;
  toolSpanCount: number;
  errorSpanCount: number;
  blockedOnUserSpanCount: number;
  totalCost: string;
  inputCost: string;
  outputCost: string;
  cacheReadCost: string;
  cacheWriteCost: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  p50DurationNs: string;
  p95DurationNs: string;
  p95LlmDurationNs: string;
  p95ToolDurationNs: string;
}

/** One per-day point on an agent's time-series chart. */
export interface AgentDailyPoint extends AgentTimeseriesPoint {
  /** ISO date (``YYYY-MM-DD``). */
  day: string;
}

export interface AgentModelToolProfileRow {
  identityValue: string;
  model: string;
  provider: string;
  executionCount: number;
  llmCallCount: number;
  toolCallCount: number;
  totalCost: string;
  totalTokens: number;
  p95DurationNs: string;
}

export interface AgentModelToolProfile {
  topModel: string;
  topProvider: string;
  rows: AgentModelToolProfileRow[];
  llmCallCount: number;
  toolCallCount: number;
  callsPerExecution: number;
  p95LlmDurationNs: string;
  p95ToolDurationNs: string;
}

export interface AgentOverview {
  summary: AgentStatsSummary;
  comparisonSummary: AgentStatsSummary | null;
  series: AgentTimeseriesPoint[];
  seriesGranularity: AgentSeriesGranularity;
  modelToolProfile: AgentModelToolProfile;
}

export interface AgentSummaryCard extends Agent, AgentStatsSummary {
  executionSeries: AgentExecutionSeriesPoint[];
  seriesGranularity: AgentSeriesGranularity;
}

/**
 * One row of the Agents index: an all-time catalog entry plus its
 * full-history headline metrics. Deliberately carries no series — the
 * sparkline comes from a separate, independently windowed read, so the
 * catalog page stays cacheable across range changes.
 */
export interface AgentIndexCard extends Agent, AgentStatsSummary {}

/** Stable position in the all-time agent catalog ordering. */
export interface AgentIndexCursor {
  lastSeenAt: string;
  identityValue: string;
}

export interface AgentIndexPage {
  items: AgentIndexCard[];
  /** Last item's ordering key, or null when the catalog is exhausted. */
  nextCursor: AgentIndexCursor | null;
}

/** Execution sparkline for one agent over the selected display window. */
export interface AgentExecutionSeries {
  identityValue: string;
  points: AgentExecutionSeriesPoint[];
}

export interface AgentExecutionSeriesWindow {
  seriesGranularity: AgentSeriesGranularity;
  /** One entry per requested identity; inactive agents carry an empty points array. */
  series: AgentExecutionSeries[];
}

/** Agent identity + its rolled-up stats over the read window. */
export interface AgentWithStats extends Agent {
  stats: AgentStatsSummary | null;
}

/**
 * Display windows an agent read accepts. ``all`` is genuine all-history,
 * not an alias for the widest preset.
 */
export const AGENT_LIST_TIME_RANGES = [
  "1h",
  "6h",
  "12h",
  "1d",
  "3d",
  "7d",
  "14d",
  "30d",
  "90d",
  "all",
] as const;
export type AgentListTimeRange = (typeof AGENT_LIST_TIME_RANGES)[number];

export const DEFAULT_AGENT_LIST_TIME_RANGE = "7d" satisfies AgentListTimeRange;
export const ALL_TIME_AGENT_LIST_TIME_RANGE =
  "all" satisfies AgentListTimeRange;
/** Widest finite preset. Not a stand-in for genuine all-history (`all`). */
export const MAX_AGENT_LIST_TIME_RANGE = "90d" satisfies AgentListTimeRange;

export const DEFAULT_AGENT_INDEX_PAGE_LIMIT = 25;
export const MAX_AGENT_INDEX_EXECUTION_SERIES_BATCH_SIZE = 100;

export function getAgentSeriesGranularity(
  timeRange: AgentListTimeRange,
): AgentSeriesGranularity {
  // Timeline policy: minute buckets through 1d, hourly through 14d,
  // then daily buckets for longer windows to keep agent/Halo queries bounded.
  switch (timeRange) {
    case "1h":
    case "6h":
    case "12h":
    case "1d":
      return "1m";
    case "3d":
    case "7d":
    case "14d":
      return "1h";
    case "30d":
    case "90d":
    case "all":
      return "1d";
  }
}

/** ******************************************************************************
 *  Output schemas (agent.* procedures)
 *
 *  Mirror the interfaces above for tRPC `.output()` validation. ClickHouse
 *  serialization rules (do not "fix" without checking real rows):
 *   - count/token fields are plain JSON numbers → `z.number()`.
 *   - cost fields (`Decimal64`) and duration fields (`UInt64` nanoseconds) are
 *     STRINGS on the wire → `z.string()`, never `z.number()`.
 *   - timestamps (`firstSeenAt`/`lastSeenAt`/series `time`/`day`) are ISO/date
 *     STRINGS, not `Date` objects → `z.string()`, not `z.date()`.
 ******************************************************************************* */

const agentIdentitySourceSchema = z.enum(AGENT_IDENTITY_SOURCES);
const agentSeriesGranularitySchema = z.enum(AGENT_SERIES_GRANULARITIES);

export const agentSchema = z.object({
  id: z.string(),
  teamId: z.string(),
  projectId: z.string(),
  identityValue: z.string(),
  identitySource: agentIdentitySourceSchema,
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
});

export const agentStatsSummarySchema = z.object({
  identityValue: z.string(),
  agentUuid: z.string(),
  identitySource: agentIdentitySourceSchema,
  executionCount: z.number(),
  successCount: z.number(),
  failureCount: z.number(),
  unknownCount: z.number(),
  spanCount: z.number(),
  llmCallCount: z.number(),
  toolCallCount: z.number(),
  errorCount: z.number(),
  llmSpanCount: z.number(),
  toolSpanCount: z.number(),
  errorSpanCount: z.number(),
  blockedOnUserCount: z.number(),
  blockedOnUserSpanCount: z.number(),
  unpricedLlmSpanCount: z.number(),
  totalCost: z.string(),
  inputCost: z.string(),
  outputCost: z.string(),
  cacheReadCost: z.string(),
  cacheWriteCost: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  totalTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheWriteTokens: z.number(),
  p50DurationNs: z.string(),
  p95DurationNs: z.string(),
  p95LlmDurationNs: z.string(),
  p95ToolDurationNs: z.string(),
  topModels: z.array(z.string()),
  topProviders: z.array(z.string()),
  topModel: z.string(),
  topProvider: z.string(),
  lastSeenAt: z.string(),
});

const agentExecutionSeriesPointSchema = z.object({
  time: z.string(),
  executionCount: z.number(),
});

const agentTimeseriesPointSchema = agentExecutionSeriesPointSchema.extend({
  successCount: z.number(),
  failureCount: z.number(),
  unknownCount: z.number(),
  errorCount: z.number(),
  spanCount: z.number(),
  llmSpanCount: z.number(),
  toolSpanCount: z.number(),
  errorSpanCount: z.number(),
  blockedOnUserSpanCount: z.number(),
  totalCost: z.string(),
  inputCost: z.string(),
  outputCost: z.string(),
  cacheReadCost: z.string(),
  cacheWriteCost: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  totalTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheWriteTokens: z.number(),
  p50DurationNs: z.string(),
  p95DurationNs: z.string(),
  p95LlmDurationNs: z.string(),
  p95ToolDurationNs: z.string(),
});

export const agentDailyPointSchema = agentTimeseriesPointSchema.extend({
  day: z.string(),
});

const agentModelToolProfileRowSchema = z.object({
  identityValue: z.string(),
  model: z.string(),
  provider: z.string(),
  executionCount: z.number(),
  llmCallCount: z.number(),
  toolCallCount: z.number(),
  totalCost: z.string(),
  totalTokens: z.number(),
  p95DurationNs: z.string(),
});

const agentModelToolProfileSchema = z.object({
  topModel: z.string(),
  topProvider: z.string(),
  rows: z.array(agentModelToolProfileRowSchema),
  llmCallCount: z.number(),
  toolCallCount: z.number(),
  callsPerExecution: z.number(),
  p95LlmDurationNs: z.string(),
  p95ToolDurationNs: z.string(),
});

export const agentOverviewSchema = z.object({
  summary: agentStatsSummarySchema,
  comparisonSummary: agentStatsSummarySchema.nullable(),
  series: z.array(agentTimeseriesPointSchema),
  seriesGranularity: agentSeriesGranularitySchema,
  modelToolProfile: agentModelToolProfileSchema,
});

export const agentSummaryCardSchema = agentSchema
  .merge(agentStatsSummarySchema)
  .extend({
    executionSeries: z.array(agentExecutionSeriesPointSchema),
    seriesGranularity: agentSeriesGranularitySchema,
  });

export const agentIndexCardSchema = agentSchema.merge(agentStatsSummarySchema);

const agentIndexCursorSchema = z.object({
  lastSeenAt: z.string(),
  identityValue: z.string(),
});

export const agentIndexPageSchema = z.object({
  items: z.array(agentIndexCardSchema),
  nextCursor: agentIndexCursorSchema.nullable(),
});

const agentExecutionSeriesSchema = z.object({
  identityValue: z.string(),
  points: z.array(agentExecutionSeriesPointSchema),
});

export const agentExecutionSeriesWindowSchema = z.object({
  seriesGranularity: agentSeriesGranularitySchema,
  series: z.array(agentExecutionSeriesSchema),
});

export const agentWithStatsSchema = agentSchema.extend({
  stats: agentStatsSummarySchema.nullable(),
});

/** Bundled first paint for the agents index (`agent.getIndexInitialData`). */
export const agentIndexInitialDataSchema = z.object({
  page: agentIndexPageSchema,
  executionSeries: agentExecutionSeriesWindowSchema,
});
